import { getNetwork, removeProtocolFromUrl } from '@functions/functions';
import { EToastVariant } from '@notifications/components/Toast/Toast.enum';
import { IToastToAdd } from '@notifications/components/Toast/Toast.type';
import useAddToast from '@store/toastsStore/hooks/useAddToast';
import { convertBigNumberToString } from '@store/web3Store/web3Store.functions';
import { useCallback, useEffect } from 'react';
import { ERROR, SUCCESS } from 'src/texts/feedbackMessages';
import { EFaucetEvent, ETopic } from '../socketStore.enum';
import { ISocketMessage } from '../socketStore.type';
import { useSocket } from './useSocket';

export const useFaucetSocketEvents = () => {
	const [socket] = useSocket();

	const { addToast } = useAddToast();

	const handleMessage = useCallback(
		(message: ISocketMessage<ETopic.faucet, EFaucetEvent>) => {
			if (message.topic !== ETopic.faucet) return;

			try {
				switch (message.event) {
					case EFaucetEvent.sent: {
						console.log('Sent');

						const typedMessage = message as ISocketMessage<ETopic.faucet, EFaucetEvent.sent>;

						const network = getNetwork(typedMessage.data.chainId);

						const newToast: IToastToAdd = {
							uuid: typedMessage.notificationId,
							variant: EToastVariant.success
						};

						if (network) {
							const { explorerUrl, ticker, decimals } = network;

							const strAmount = convertBigNumberToString(typedMessage.data.value, decimals);

							// keel only first  10 chars of tx hash
							const linkLabel =
								removeProtocolFromUrl(explorerUrl) + 'tx/' + typedMessage.data.hash.slice(0, 10) + '...';

							newToast.title = SUCCESS.faucet.sending.title(strAmount, ticker);
							newToast.message = SUCCESS.faucet.sending.message(
								explorerUrl + 'tx/' + typedMessage.data.hash,
								linkLabel
							);
						} else {
							newToast.title = SUCCESS.faucet.sending.fallbackTitle;
							newToast.message = SUCCESS.faucet.sending.fallbackMessage;
						}

						addToast(newToast);
						break;
					}
					case EFaucetEvent.processed: {
						console.log('Processed');

						const typedMessage = message as ISocketMessage<ETopic.faucet, EFaucetEvent.sent>;

						const network = getNetwork(typedMessage.data.chainId);

						const newToast: IToastToAdd = {
							uuid: typedMessage.notificationId,
							variant: EToastVariant.success
						};

						if (network) {
							const { explorerUrl, ticker, decimals } = network;

							const strAmount = convertBigNumberToString(typedMessage.data.value, decimals);

							// keel only first  10 chars of tx hash
							const linkLabel =
								removeProtocolFromUrl(explorerUrl) + 'tx/' + typedMessage.data.hash.slice(0, 10) + '...';

							newToast.title = SUCCESS.faucet.sent.title(strAmount, ticker);
							newToast.message = SUCCESS.faucet.sent.message(explorerUrl + 'tx/' + typedMessage.data.hash, linkLabel);
						} else {
							newToast.title = SUCCESS.faucet.sent.fallbackTitle;
							newToast.message = SUCCESS.faucet.sent.fallbackMessage;
						}

						addToast(newToast);
						break;
					}
					case EFaucetEvent.failed: {
						console.log('Failed');

						const typedMessage = message as ISocketMessage<ETopic.faucet, EFaucetEvent.sent>;

						const network = getNetwork(typedMessage.data.chainId);

						const newToast: IToastToAdd = {
							uuid: typedMessage.notificationId,
							variant: EToastVariant.success
						};

						if (network) {
							const { explorerUrl, ticker } = network;

							// keel only first  10 chars of tx hash
							const linkLabel =
								removeProtocolFromUrl(explorerUrl) + 'tx/' + typedMessage.data.hash.slice(0, 10) + '...';

							newToast.title = ERROR.faucet.sent.title(ticker);
							newToast.message = ERROR.faucet.sent.message(explorerUrl + 'tx/' + typedMessage.data.hash, linkLabel);
						} else {
							newToast.title = ERROR.faucet.sent.fallbackTitle;
							newToast.message = ERROR.faucet.sent.fallbackMessage;
						}

						addToast(newToast);
						break;
					}
					default:
						break;
				}
			} catch (err) {
				console.error('useFaucetSocketEvents', err);
			}
		},
		[addToast]
	);

	useEffect(() => {
		if (socket) {
			socket.connect();

			// fired when socket is successfully connected
			socket.on('connect', () => {
				console.debug(ETopic.faucet, 'socket connected');
			});

			socket.on(ETopic.faucet, handleMessage);

			return () => {
				socket.off('connect');
				socket.off(ETopic.faucet, handleMessage);
				socket.close();
			};
		}
	}, [handleMessage, socket]);
};
